import { ReactElement, useContext } from 'react'

import { CollectionSellingMethodType, Visibility } from 'globalTypes'

import { gql, useQuery } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { Trans } from '@lingui/macro'
import CollectionShareWrapper from 'Root/App/Card/Collection/ShareWrapper'
import ContentShareWrapper from 'Root/App/Card/Content/ShareWrapper'
import FolderShareWrapper from 'Root/App/Card/Folder/ShareWrapper'
import { Context as AuthorizationsContext } from 'Root/App/Contexts/Authorizations'
import Paragraph from 'Root/App/Paragraph'
import Title from 'Root/App/Title'
import { getElementType } from 'lib/get-element-type'
import { useCurrentUserId } from 'lib/use-current-user-id'
import filter from 'lodash/filter'
import map from 'lodash/map'

import AddUser from './AddUser'
import Loading from './Loading'
import Authorization from './Row/Authorization'
import Invite from './Row/Invite'
import CopyCheckoutLink from './copy-checkout-link'
import QUERY from './fetch-element-authorizations.graphql'
import GenerateLink from './generate-link'
import UpdateSharePanelVisibility from './update-visibility'

library.add(faCheck)

const GET_SHARED_ELEMENT_VISIBILITY = gql`
  query GetSharedElementVisibility($id: ID!) {
    element(id: $id) {
      __typename

      ... on Collection {
        id
        visibility
      }

      ... on Content {
        id
        visibility
      }

      ... on Folder {
        id
        visibility
      }
    }
  }
`

interface Props {
  id: string
}

const ShareWrapperSwitch = ({ children, element }) => {
  const type = getElementType(element.__typename)

  return type === 'collection' ? (
    <CollectionShareWrapper collection={element as any}>
      {children}
    </CollectionShareWrapper>
  ) : type === 'folder' ? (
    <FolderShareWrapper folder={element as any}>{children}</FolderShareWrapper>
  ) : (
    <ContentShareWrapper content={element as any}>
      {children}
    </ContentShareWrapper>
  )
}

const ShareSectionHeading = ({
  title,
  subtitle,
}: {
  title: ReactElement
  subtitle: ReactElement
}) => (
  <span className="flex flex-col gap-1">
    <Title size="XS">{title}</Title>
    <Paragraph>{subtitle}</Paragraph>
  </span>
)

const ShareBox = ({ id, closePanel }) => {
  const userId = useCurrentUserId()

  const { data, loading } = useQuery(QUERY, {
    variables: {
      id,
    },
  })

  const {
    data: sharedElementVisibility,
    loading: loadingSharedElementVisibility,
  } = useQuery(GET_SHARED_ELEMENT_VISIBILITY, {
    variables: {
      id,
    },
  })

  if (loading || loadingSharedElementVisibility) {
    return <Loading />
  }

  const element = data?.element

  if (sharedElementVisibility?.element?.visibility) {
    element.visibility =
      sharedElementVisibility?.element?.visibility || 'private'
  }

  const rows = filter(
    element?.authorizations || [],
    ({ __typename, ...data }) =>
      __typename === 'Invite' ||
      !('user' in data) ||
      ('user' in data && data.user.id !== userId)
  )

  const elementType = getElementType(element?.__typename)

  const showCheckoutLink =
    elementType === 'collection' &&
    element.visibility === Visibility.public &&
    element.selling &&
    element.sellingPricing > 0 &&
    element.sellingMethod === CollectionSellingMethodType.online

  const handleOnConfirm = (visibility: Visibility) => {
    if (visibility === Visibility.mine) {
      closePanel()
    }
  }

  return (
    <>
      {elementType !== 'folder' && (
        <UpdateSharePanelVisibility
          id={id}
          element={element}
          type={elementType}
          onConfirm={handleOnConfirm}
        />
      )}

      <ShareWrapperSwitch element={element}>
        {showCheckoutLink && (
          <span id="share-by-checkout" className="flex flex-col gap-4">
            <ShareSectionHeading
              title={<Trans>Link al checkout</Trans>}
              subtitle={
                <Trans>
                  Copia il link al checkout del tuo corso per integrarlo nel tuo
                  sito.
                </Trans>
              }
            />

            <CopyCheckoutLink slug={element.slug} />
          </span>
        )}

        <span id="share-by-token" className="flex flex-col gap-4">
          <ShareSectionHeading
            title={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Condividi tramite link privato</Trans>
              ) : (
                <Trans>Condividi tramite link</Trans>
              )
            }
            subtitle={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Genera un link privato valido una sola volta.</Trans>
              ) : (
                <Trans>Copia il link per condividerlo con chi vuoi.</Trans>
              )
            }
          />

          <GenerateLink visibility={element.visibility} />
        </span>
      </ShareWrapperSwitch>

      <span id="share-by-email" className="flex flex-col gap-4">
        <ShareSectionHeading
          title={<Trans>Condividi tramite email</Trans>}
          subtitle={
            <Trans>
              Manderemo alle persone che inviti un link magico e le istruzioni
              per accedere.
            </Trans>
          }
        />
        <AddUser
          id={element.id}
          knownUsers={data.knownUsers}
          creator={element.creator}
          currentUserData={data.user.emails}
        />
      </span>

      <div role="list" className="flex flex-col divide-y divide-gray-200">
        {rows.length > 0 && (
          <Title size="XS" className="mb-4">
            <Trans>Condiviso con</Trans>:
          </Title>
        )}
        {map(rows, (row) => {
          switch (row.__typename) {
            case 'Authorization':
              return (
                <Authorization
                  authorization={row}
                  elementId={element.id}
                  key={row.id}
                  __typename={element.__typename}
                />
              )
            case 'Invite':
              return <Invite elementId={element.id} invite={row} key={row.id} />
          }
        })}
      </div>
    </>
  )
}

const SlideContainer = ({ id }: Props) => {
  const { setElementId, subscriptionPlanCode, userSubscriptionLoading } =
    useContext(AuthorizationsContext)

  if (userSubscriptionLoading) return null

  const shareUserElement =
    subscriptionPlanCode && subscriptionPlanCode !== 'essential'

  return (
    <div className="flex flex-col mx-auto px-4 py-3 max-w-3xl sm:px-6 sm:py-6 gap-6">
      <Title size="M">
        <Trans>Condividi</Trans>
      </Title>

      {shareUserElement && (
        <ShareBox id={id} closePanel={() => setElementId(undefined)} />
      )}
    </div>
  )
}

export default SlideContainer
